import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { consignmentShipApi, FM160, FM161, ConsignmentShipCreateRequest } from '../../../services/consignmentShipApi';

export type FormMode = 'browse' | 'create' | 'edit';

const emptyMaster = (): Partial<FM160> => ({
  fm160_01: '',
  fm160_11: new Date().toISOString().slice(0, 10),
  fm160_12: '',
  fm160_13: 'NTD',
  fm160_14: 1,
  fm160_15: '',
  fm160_16: '',
  fm160_17: 0,
  fm160_18: 0,
  fm160_19: 0,
  fm160_20: '',
  fm160_21: '',
  fm160_22: '',
  fm160_30: 0,
  fm160_80: 'N',
  fm160_91: '',
  fm160_92: null,
});

const emptyDetail = (): Partial<FM161> => ({
  fm161_01: '',
  fm161_02: 0,
  fm161_11: '',
  fm161_12: '',
  fm161_13: '',
  fm161_14: 0,
  fm161_15: 0,
  fm161_16: 0,
  fm161_17: 1,
  fm161_18: '',
  fm161_19: 0,
  fm161_20: '',
});

export function useConsignmentShipForm() {
  const [mode, setMode] = useState<FormMode>('browse');
  const [loading, setLoading] = useState(false);
  const [master, setMaster] = useState<Partial<FM160>>(emptyMaster());
  const [details, setDetails] = useState<Partial<FM161>[]>([]);
  const [records, setRecords] = useState<FM160[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [currentId, setCurrentId] = useState<string | null>(null);

  // Load list
  const loadList = useCallback(async (p?: number, kw?: string) => {
    setLoading(true);
    try {
      const res = await consignmentShipApi.list(p || page, pageSize, (kw ?? keyword) || undefined);
      setRecords(res.data || []);
      setTotal(res.total);
    } catch {
      message.error('載入清單失敗');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword]);

  useEffect(() => { loadList(); }, [loadList]);

  // Load single record
  const loadRecord = useCallback(async (orderNo: string) => {
    setLoading(true);
    try {
      const rec = await consignmentShipApi.get(orderNo);
      setMaster(rec);
      setDetails(rec.details || []);
      setCurrentId(rec.fm160_01);
      setMode('browse');
    } catch {
      message.error('載入記錄失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  // Start create mode
  const startCreate = useCallback(() => {
    setMaster(emptyMaster());
    setDetails([]);
    setCurrentId(null);
    setMode('create');
  }, []);

  // Start edit mode
  const startEdit = useCallback(() => {
    if (!currentId) return;
    setMode('edit');
  }, [currentId]);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    if (currentId) {
      loadRecord(currentId);
    } else {
      setMaster(emptyMaster());
      setDetails([]);
      setMode('browse');
    }
  }, [currentId, loadRecord]);

  // Add detail row
  const addDetail = useCallback(() => {
    if (details.length >= 99) {
      message.warning('明細最多99筆');
      return;
    }
    setDetails(prev => [...prev, { ...emptyDetail(), fm161_02: prev.length + 1 }]);
  }, [details.length]);

  // Remove detail row
  const removeDetail = useCallback((index: number) => {
    setDetails(prev => prev.filter((_, i) => i !== index).map((d, i) => ({ ...d, fm161_02: i + 1 })));
  }, []);

  // Update detail row
  const updateDetail = useCallback((index: number, field: string, value: unknown) => {
    setDetails(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  }, []);

  // Update master field
  const updateMaster = useCallback((field: string, value: unknown) => {
    setMaster(prev => ({ ...prev, [field]: value }));
  }, []);

  // Save (create or update)
  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload: ConsignmentShipCreateRequest = {
        master: master,
        details: details,
      };

      let result: FM160;
      if (mode === 'create') {
        result = await consignmentShipApi.create(payload);
        message.success('新增成功');
      } else {
        result = await consignmentShipApi.update(currentId!, payload);
        message.success('更新成功');
      }
      setMaster(result);
      setDetails(result.details || []);
      setCurrentId(result.fm160_01);
      setMode('browse');
      loadList();
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { errors?: Array<{ field: string; message: string }> } } };
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const msgs = error.response.data.errors.map((e: { message: string }) => e.message).join('\n');
        message.error(msgs);
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [master, details, mode, currentId, loadList]);

  // Delete
  const deleteRecord = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      await consignmentShipApi.delete(currentId);
      message.success('刪除成功');
      setMaster(emptyMaster());
      setDetails([]);
      setCurrentId(null);
      setMode('browse');
      loadList();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [currentId, loadList]);

  // Search
  const search = useCallback((kw: string) => {
    setKeyword(kw);
    setPage(1);
    loadList(1, kw);
  }, [loadList]);

  return {
    // State
    mode, loading, master, details, records, total, page, pageSize, keyword, currentId,
    // Master actions
    updateMaster, setPage,
    // Detail actions
    addDetail, removeDetail, updateDetail,
    // Form actions
    startCreate, startEdit, cancelEdit, save, deleteRecord,
    // Navigation
    loadRecord, loadList, search,
    // Derived
    isApproved: master.fm160_80 === 'P',
    isEditing: mode === 'create' || mode === 'edit',
  };
}
